import React, { useState, useRef } from "react";
import Swal from "sweetalert2";
import SmartText from "../SmartText";
import { regenerateSingleQuestion } from "../../services/api";

const inferQuizType = (q) => {
  const options = Array.isArray(q?.options) ? q.options : [];
  const question = String(q?.question || "");

  if (options.length >= 2) {
    const normalized = options.map((o) => String(o || "").trim().toLowerCase());
    if (normalized.includes("true") && normalized.includes("false")) return "true-false";
    return "mcq";
  }
  if (question.includes("_______") || question.includes("____")) return "fill-blanks";
  return "identification";
};

const QuizEditor = ({
  questions = [],
  config = {},
  onUpdateQuiz,
  originalContent = "",
  regenLimit = null,
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [regeneratingIndex, setRegeneratingIndex] = useState(null);
  const [recentlyDeleted, setRecentlyDeleted] = useState(null);
  // Tracks total AI question regenerations for this entire reviewer
  const totalRegenCountRef = useRef(0);
  const [, setRegenTick] = useState(0);

  // --- Handlers for Question Edit ---
  const handleStartEdit = (index) => {
    const targetQ = questions[index];
    if (!targetQ) return;

    setEditingIndex(index);
    setEditFormData({
      question: targetQ.question || "",
      options: Array.isArray(targetQ.options) ? [...targetQ.options] : [],
      answer: targetQ.answer || "",
      explanation: targetQ.explanation || "",
      type: inferQuizType(targetQ),
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditFormData(null);
  };

  const handleSaveEdit = (index) => {
    if (!editFormData) return;

    const trimmedQuestion = String(editFormData.question || "").trim();
    if (!trimmedQuestion) {
      Swal.fire("Validation Error", "Question text cannot be empty.", "warning");
      return;
    }

    const trimmedAnswer = String(editFormData.answer || "").trim();
    if (!trimmedAnswer && editFormData.options.length === 0) {
      Swal.fire("Validation Error", "Please provide a valid answer.", "warning");
      return;
    }

    let finalOptions = editFormData.options.map((opt) => String(opt || "").trim()).filter(Boolean);
    let finalAnswer = trimmedAnswer;

    // For MCQ: make sure answer matches one of the options
    if (editFormData.type === "mcq" || editFormData.type === "true-false") {
      if (finalOptions.length < 2) {
        Swal.fire("Validation Error", "Please provide at least 2 options.", "warning");
        return;
      }
      if (!finalOptions.includes(finalAnswer)) {
        finalAnswer = finalOptions[0];
      }
    }

    const updatedItem = {
      question: trimmedQuestion,
      options: finalOptions,
      answer: finalAnswer,
      explanation: String(editFormData.explanation || "").trim(),
    };

    const updatedList = [...questions];
    updatedList[index] = updatedItem;

    if (onUpdateQuiz) onUpdateQuiz(updatedList);

    setEditingIndex(null);
    setEditFormData(null);

    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Question updated",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  // Option modifications during edit
  const handleOptionChange = (optIdx, val) => {
    if (!editFormData) return;
    const nextOptions = [...editFormData.options];
    const prevVal = nextOptions[optIdx];
    nextOptions[optIdx] = val;

    let nextAnswer = editFormData.answer;
    if (nextAnswer === prevVal) {
      nextAnswer = val;
    }

    setEditFormData({
      ...editFormData,
      options: nextOptions,
      answer: nextAnswer,
    });
  };

  const handleAddOption = () => {
    if (!editFormData) return;
    if (editFormData.options.length >= 6) {
      Swal.fire("Limit Reached", "Maximum 6 choices per question.", "info");
      return;
    }
    const newOpt = `Option ${editFormData.options.length + 1}`;
    setEditFormData({
      ...editFormData,
      options: [...editFormData.options, newOpt],
    });
  };

  const handleRemoveOption = (optIdx) => {
    if (!editFormData || editFormData.options.length <= 2) {
      Swal.fire("Warning", "At least 2 choices are required.", "warning");
      return;
    }
    const removedVal = editFormData.options[optIdx];
    const nextOptions = editFormData.options.filter((_, i) => i !== optIdx);
    let nextAnswer = editFormData.answer;

    if (nextAnswer === removedVal) {
      nextAnswer = nextOptions[0] || "";
    }

    setEditFormData({
      ...editFormData,
      options: nextOptions,
      answer: nextAnswer,
    });
  };

  // --- Handlers for Add / Delete / Shuffle ---
  const handleAddNewQuestion = () => {
    const newQ = {
      question: "New quiz question...",
      options: ["Option A", "Option B", "Option C", "Option D"],
      answer: "Option A",
      explanation: "",
    };
    const updatedList = [...questions, newQ];
    if (onUpdateQuiz) onUpdateQuiz(updatedList);

    // Open edit mode immediately for the new question
    const newIndex = updatedList.length - 1;
    setEditingIndex(newIndex);
    setEditFormData({
      question: newQ.question,
      options: [...newQ.options],
      answer: newQ.answer,
      explanation: newQ.explanation,
      type: "mcq",
    });
  };

  const handleDeleteQuestion = (index) => {
    const qToDelete = questions[index];
    if (!qToDelete) return;

    if (questions.length <= 1) {
      Swal.fire("Warning", "A quiz must have at least one question.", "warning");
      return;
    }

    const updatedList = questions.filter((_, i) => i !== index);
    setRecentlyDeleted({ item: qToDelete, index });
    if (onUpdateQuiz) onUpdateQuiz(updatedList);

    if (editingIndex === index) {
      setEditingIndex(null);
      setEditFormData(null);
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleUndoDelete = () => {
    if (!recentlyDeleted) return;
    const { item, index } = recentlyDeleted;
    const updatedList = [...questions];
    updatedList.splice(index, 0, item);
    if (onUpdateQuiz) onUpdateQuiz(updatedList);
    setRecentlyDeleted(null);
  };

  const handleShuffleQuestions = () => {
    if (questions.length <= 1) return;
    const shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    if (onUpdateQuiz) onUpdateQuiz(shuffled);
    setEditingIndex(null);
    setEditFormData(null);

    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "info",
      title: "Questions shuffled",
      showConfirmButton: false,
      timer: 1500,
    });
  };

  // --- Handlers for Single Question Regeneration ---
  const handleRegenerateQuestion = async (index) => {
    const targetQ = questions[index];
    if (!targetQ) return;

    const currentTotal = totalRegenCountRef.current;
    if (regenLimit != null && currentTotal >= regenLimit) {
      Swal.fire({
        icon: "warning",
        title: "Reviewer Limit Reached",
        text: `You have reached the limit of ${regenLimit} question changes for this reviewer. Please edit questions manually instead.`,
        confirmButtonColor: "#4f46e5",
      });
      return;
    }

    if (!originalContent || String(originalContent).trim().length < 30) {
      Swal.fire(
        "Content Missing",
        "Original lesson content is needed to regenerate questions accurately.",
        "warning"
      );
      return;
    }

    setRegeneratingIndex(index);

    try {
      const existingQuestionTexts = questions
        .map((q, idx) => (idx !== index ? q.question : null))
        .filter(Boolean);

      const targetType = inferQuizType(targetQ);

      const response = await regenerateSingleQuestion({
        content: originalContent,
        existingQuestions: existingQuestionTexts,
        quizType: targetType,
        difficulty: config.difficulty || 2,
        customInstructions: config.customInstructions || "",
        userInstruction: config.customInstructions || "",
      });

      if (response?.success && response?.data) {
        const regeneratedItem = response.data;
        const updatedList = [...questions];
        updatedList[index] = regeneratedItem;

        // Synchronously increment total reviewer regeneration count
        const newTotal = totalRegenCountRef.current + 1;
        totalRegenCountRef.current = newTotal;
        setRegenTick((t) => t + 1);

        if (onUpdateQuiz) onUpdateQuiz(updatedList);

        if (editingIndex === index) {
          setEditingIndex(null);
          setEditFormData(null);
        }

        const remaining = regenLimit != null ? Math.max(0, regenLimit - newTotal) : null;
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `Question #${index + 1} regenerated!`,
          text:
            remaining != null
              ? remaining > 0
                ? `${remaining} change${remaining > 1 ? "s" : ""} left for this reviewer.`
                : "You have used all 3 question changes for this reviewer."
              : undefined,
          showConfirmButton: false,
          timer: 2500,
        });
      } else {
        throw new Error(response?.error || "Failed to regenerate question.");
      }
    } catch (err) {
      console.error("Regenerate question error:", err);
      Swal.fire("Regeneration Failed", err.message || "Could not generate replacement question.", "error");
    } finally {
      setRegeneratingIndex(null);
    }
  };

  const totalUsed = totalRegenCountRef.current;
  const hasLimit = regenLimit != null;
  const totalRemaining = hasLimit ? Math.max(0, regenLimit - totalUsed) : Infinity;
  const regenExhausted = hasLimit && totalUsed >= regenLimit;

  return (
    <div className="space-y-6">
      {/* --- Top Banner / Stats Toolbar --- */}
      <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-blue-100/80 p-4 sm:p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center text-lg shadow-md shadow-blue-500/20">
            <i className="fa-solid fa-wand-magic-sparkles"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-gray-800 text-base sm:text-lg">
                Quiz Questions
              </h3>
              <span className="bg-blue-600 text-white text-xs font-black px-2.5 py-0.5 rounded-full shadow-sm">
                {questions.length}
              </span>
              {hasLimit && (
                <span
                  className={`text-xs font-bold px-2.5 py-0.5 rounded-full border flex items-center gap-1 ${
                    regenExhausted
                      ? "bg-red-50 text-red-700 border-red-200"
                      : totalRemaining === 1
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-indigo-50 text-indigo-700 border-indigo-200"
                  }`}
                >
                  <i className="fa-solid fa-arrows-rotate text-[10px]"></i>
                  <span>
                    {regenExhausted
                      ? "0/3 Changes Left"
                      : `${totalRemaining}/3 Changes Left`}
                  </span>
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Review, edit choices, or regenerate AI questions before saving.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <button
            type="button"
            onClick={handleShuffleQuestions}
            title="Randomize question order"
            className="px-3.5 py-2 text-xs font-bold rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition shadow-sm flex items-center gap-1.5"
          >
            <i className="fa-solid fa-shuffle text-gray-400"></i>
            <span>Shuffle</span>
          </button>

          <button
            type="button"
            onClick={handleAddNewQuestion}
            className="px-4 py-2 text-xs font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] transition shadow-md shadow-blue-500/20 flex items-center gap-1.5 ml-auto sm:ml-0"
          >
            <i className="fa-solid fa-plus"></i>
            <span>Add Question</span>
          </button>
        </div>
      </div>

      {/* --- Undo Banner (if item was just deleted) --- */}
      {recentlyDeleted && (
        <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-xl flex items-center justify-between text-amber-900 text-xs sm:text-sm animate-fadeIn">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-trash-can text-amber-600"></i>
            <span>Question removed from review list.</span>
          </div>
          <button
            type="button"
            onClick={handleUndoDelete}
            className="font-bold text-amber-800 underline hover:text-amber-950 px-2 py-1"
          >
            Undo
          </button>
        </div>
      )}

      {/* --- Questions Cards List --- */}
      <div className="space-y-4">
        {questions.map((q, idx) => {
          const isEditing = editingIndex === idx;
          const isRegenerating = regeneratingIndex === idx;
          const qType = inferQuizType(q);

          return (
            <div
              key={idx}
              className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${
                isEditing
                  ? "border-blue-500 ring-4 ring-blue-50 shadow-md"
                  : "border-gray-200/80 hover:border-gray-300 shadow-sm hover:shadow"
              }`}
            >
              {/* --- CARD HEADER: Number, Type Badge, and Actions --- */}
              <div className="px-5 py-3.5 bg-gray-50/70 border-b border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 font-black text-xs flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-md">
                    {qType === "mcq"
                      ? "Multiple Choice"
                      : qType === "true-false"
                      ? "True / False"
                      : qType === "fill-blanks"
                      ? "Fill in Blank"
                      : "Identification"}
                  </span>
                </div>

                {/* Question Action Buttons */}
                <div className="flex items-center gap-1.5">
                  {!isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleStartEdit(idx)}
                        disabled={isRegenerating}
                        title="Edit question & choices"
                        className="px-2.5 py-1.5 text-xs font-bold rounded-lg text-gray-600 bg-white border border-gray-200 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition flex items-center gap-1.5"
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                        <span className="hidden sm:inline">Edit</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleRegenerateQuestion(idx)}
                        disabled={isRegenerating || regenExhausted}
                        title={
                          regenExhausted
                            ? "Reviewer limit reached (3/3 changes used)"
                            : hasLimit
                            ? `Regenerate with AI (${totalRemaining} reviewer change${totalRemaining > 1 ? "s" : ""} left)`
                            : "Regenerate this question with AI"
                        }
                        className={`px-2.5 py-1.5 text-xs font-bold rounded-lg border transition flex items-center gap-1.5 disabled:opacity-60 ${
                          regenExhausted
                            ? "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"
                            : hasLimit && totalRemaining === 1
                            ? "text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300"
                            : "text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-100 hover:border-indigo-200"
                        }`}
                      >
                        {isRegenerating ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin"></i>
                            <span className="hidden sm:inline">Regenerating...</span>
                          </>
                        ) : regenExhausted ? (
                          <>
                            <i className="fa-solid fa-ban"></i>
                            <span className="hidden sm:inline">Limit Reached</span>
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-arrows-rotate"></i>
                            <span className="hidden sm:inline">Regenerate</span>
                            {hasLimit && (
                              <span
                                className={`text-[10px] font-black px-1 py-0 rounded ${
                                  totalRemaining === 1
                                    ? "bg-amber-200 text-amber-800"
                                    : "bg-indigo-100 text-indigo-700"
                                }`}
                              >
                                {totalRemaining}
                              </span>
                            )}
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteQuestion(idx)}
                        disabled={isRegenerating}
                        title="Delete question"
                        className="w-8 h-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition flex items-center justify-center text-xs"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-3 py-1.5 text-xs font-bold rounded-lg text-gray-500 hover:bg-gray-100 transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(idx)}
                        className="px-4 py-1.5 text-xs font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition flex items-center gap-1.5"
                      >
                        <i className="fa-solid fa-check"></i>
                        <span>Save</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* --- CARD BODY: View Mode vs Edit Mode --- */}
              <div className="p-5">
                {isRegenerating ? (
                  <div className="py-8 flex flex-col items-center justify-center text-center">
                    <div className="w-10 h-10 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3"></div>
                    <p className="font-bold text-gray-700 text-sm">Generating new question...</p>
                    <p className="text-xs text-gray-400 mt-1">Analyzing lesson context with AI</p>
                  </div>
                ) : isEditing && editFormData ? (
                  /* --- EDIT FORM MODE --- */
                  <div className="space-y-4 animate-fadeIn">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        Question Text
                      </label>
                      <textarea
                        value={editFormData.question}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, question: e.target.value })
                        }
                        rows={3}
                        className="w-full p-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm font-semibold text-gray-800 transition resize-y"
                        placeholder="Enter the question text..."
                      />
                    </div>

                    {/* Choices / Options for MCQ & True-False */}
                    {(editFormData.type === "mcq" || editFormData.type === "true-false") && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Answer Choices (Select the correct answer circle)
                          </label>
                          {editFormData.type === "mcq" && editFormData.options.length < 6 && (
                            <button
                              type="button"
                              onClick={handleAddOption}
                              className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <i className="fa-solid fa-plus text-[10px]"></i> Add Choice
                            </button>
                          )}
                        </div>

                        <div className="space-y-2">
                          {editFormData.options.map((opt, optIdx) => {
                            const isSelectedAnswer = editFormData.answer === opt;
                            return (
                              <div
                                key={optIdx}
                                className={`flex items-center gap-2 p-2 rounded-xl border transition ${
                                  isSelectedAnswer
                                    ? "bg-green-50/70 border-green-300"
                                    : "bg-white border-gray-200"
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditFormData({ ...editFormData, answer: opt })
                                  }
                                  title="Mark as correct answer"
                                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition ${
                                    isSelectedAnswer
                                      ? "bg-green-600 text-white shadow-sm"
                                      : "border-2 border-gray-300 hover:border-green-500 text-transparent"
                                  }`}
                                >
                                  <i className="fa-solid fa-check text-[10px]"></i>
                                </button>

                                <span className="font-mono text-xs text-gray-400 font-bold w-4">
                                  {String.fromCharCode(65 + optIdx)}.
                                </span>

                                <input
                                  type="text"
                                  value={opt}
                                  onChange={(e) => handleOptionChange(optIdx, e.target.value)}
                                  className="flex-1 bg-transparent border-0 outline-none text-sm font-medium text-gray-800 px-1"
                                  placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                                />

                                {editFormData.type === "mcq" && editFormData.options.length > 2 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveOption(optIdx)}
                                    title="Remove this option"
                                    className="w-7 h-7 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition"
                                  >
                                    <i className="fa-solid fa-xmark text-xs"></i>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Direct Answer input for Fill-Blanks & Identification */}
                    {(editFormData.type === "fill-blanks" ||
                      editFormData.type === "identification") && (
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                          Correct Answer
                        </label>
                        <input
                          type="text"
                          value={editFormData.answer}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, answer: e.target.value })
                          }
                          className="w-full p-3 rounded-xl border border-green-300 bg-green-50/50 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none text-sm font-bold text-green-900 transition"
                          placeholder="Type the exact correct answer..."
                        />
                      </div>
                    )}

                    {/* Explanation */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        Explanation (Optional)
                      </label>
                      <textarea
                        value={editFormData.explanation}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, explanation: e.target.value })
                        }
                        rows={2}
                        className="w-full p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-xs font-medium text-gray-700 transition"
                        placeholder="Explain why this answer is correct..."
                      />
                    </div>
                  </div>
                ) : (
                  /* --- VIEW MODE --- */
                  <div className="space-y-4">
                    {/* Question text */}
                    <div className="font-bold text-gray-900 text-sm sm:text-base leading-relaxed">
                      <SmartText content={q.question} />
                    </div>

                    {/* Choices list for MCQ / True-False */}
                    {Array.isArray(q.options) && q.options.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        {q.options.map((opt, optIdx) => {
                          const isCorrect = opt === q.answer;
                          return (
                            <div
                              key={optIdx}
                              className={`p-3 rounded-xl border text-xs sm:text-sm font-medium flex items-start gap-2.5 transition ${
                                isCorrect
                                  ? "bg-green-50/80 border-green-200 text-green-900 font-bold shadow-xs"
                                  : "bg-gray-50/60 border-gray-200/80 text-gray-700"
                              }`}
                            >
                              <span
                                className={`w-5 h-5 rounded-md font-mono text-xs flex items-center justify-center flex-shrink-0 ${
                                  isCorrect
                                    ? "bg-green-600 text-white font-bold"
                                    : "bg-gray-200 text-gray-600"
                                }`}
                              >
                                {String.fromCharCode(65 + optIdx)}
                              </span>
                              <div className="flex-1 pt-0.5">
                                <SmartText content={opt} />
                              </div>
                              {isCorrect && (
                                <span className="text-green-600 text-xs flex items-center gap-1 font-bold ml-auto flex-shrink-0">
                                  <i className="fa-solid fa-check"></i> Correct
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    {/* Direct answer highlight for Identification & Fill-in-blanks */}
                    {(qType === "fill-blanks" || qType === "identification") && (
                      <div className="inline-flex items-center gap-2 p-3 rounded-xl bg-green-50 border border-green-200 text-green-900 text-xs sm:text-sm font-bold">
                        <i className="fa-solid fa-check-circle text-green-600 text-base"></i>
                        <span>Correct Answer:</span>
                        <span className="underline decoration-green-500">
                          <SmartText content={q.answer} />
                        </span>
                      </div>
                    )}

                    {/* Explanation snippet if available */}
                    {q.explanation && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-start gap-2 text-xs text-gray-500 bg-slate-50/50 p-2.5 rounded-lg">
                        <i className="fa-solid fa-lightbulb text-amber-500 mt-0.5 flex-shrink-0"></i>
                        <div>
                          <span className="font-bold text-gray-600">Explanation: </span>
                          <SmartText content={q.explanation} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Add Question Quick Action */}
      <div className="pt-2 flex justify-center">
        <button
          type="button"
          onClick={handleAddNewQuestion}
          className="px-6 py-3 text-sm font-bold rounded-2xl bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/30 transition-all flex items-center gap-2 shadow-xs"
        >
          <i className="fa-solid fa-plus-circle text-base"></i>
          <span>Add Another Question</span>
        </button>
      </div>
    </div>
  );
};

export default QuizEditor;
